import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface IntegrationCardProps {
  title: string;
  icon: React.ReactNode;
  iconBgColor: string;
  iconTextColor: string;
  buttonText: string;
  onAction: () => void;
  isComingSoon?: boolean;
  isConnected?: boolean;
  onDisconnect?: () => void;
  onViewStatus?: () => void;
  dataTour?: string; // Added for tour
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  title,
  icon,
  iconBgColor,
  iconTextColor,
  buttonText,
  onAction,
  isComingSoon = false,
  isConnected = false,
  onDisconnect,
  onViewStatus,
  dataTour, // Destructure dataTour prop
}) => {
  const { t } = useTranslation();

  const primaryButtonClasses = isComingSoon
    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
    : 'bg-primary text-white hover:opacity-90 transition-opacity';

  const renderActionButtons = () => {
    // Caso 1: Conectado (WhatsApp ou SerpApi)
    if (isConnected && onDisconnect) {
      // Se for SerpApi, mostramos apenas Desconectar
      if (title === t('integrations.serpapi_title')) {
        return (
          <motion.button
            onClick={onDisconnect}
            className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-red-500 text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-red-600 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="material-symbols-outlined text-lg">link_off</span>
            {t('integrations.whatsapp_button_disconnect')}
          </motion.button>
        );
      }

      // Caso 1.1: WhatsApp (mantém Status e Desconectar) - AGORA EM COLUNA
      if (onViewStatus) {
        return (
          <div className="flex w-full flex-col gap-3">
            <motion.button
              onClick={onViewStatus}
              className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-4 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="material-symbols-outlined text-lg">info</span>
              <span>{t('integrations.whatsapp_button_status')}</span>
            </motion.button>
            <motion.button
              onClick={onDisconnect}
              // Usando px-6 para dar mais espaço horizontal
              className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-red-500 text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-red-600 transition-opacity"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="material-symbols-outlined text-lg">link_off</span>
              <span>{t('integrations.whatsapp_button_disconnect')}</span>
            </motion.button>
          </div>
        );
      }
    }

    // Caso 2: Default button (Connect/Configure/Coming Soon)
    return (
      <button
        onClick={isComingSoon ? undefined : onAction}
        disabled={isComingSoon}
        // Usando px-6 para o botão primário
        className={`flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 ${primaryButtonClasses}`}
      >
        {buttonText}
      </button>
    );
  };

  const renderStatusBadge = () => {
    if (isComingSoon) {
      return (
        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary dark:bg-primary/20 dark:text-orange-300">
          {t('integrations.status_coming_soon')}
        </span>
      );
    }

    if (isConnected) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/50 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-300">
          {t('integrations.status_connected')}
        </span>
      );
    }

    // Se não estiver conectado e não for 'Em Breve'
    if (title === t('integrations.serpapi_title') || title === t('integrations.whatsapp_title')) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/50 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-300">
          {t('integrations.status_disconnected')}
        </span>
      );
    }

    return null;
  };

  return (
    <motion.div
      data-tour={dataTour} // Apply dataTour prop here
      className={`flex flex-col gap-6 p-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-lg transition-all duration-300 card ${isComingSoon ? 'opacity-60' : 'hover-lift cursor-pointer'}`}
      whileHover={isComingSoon || isConnected ? {} : { scale: 1.02 }}
      whileTap={isComingSoon || isConnected ? {} : { scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      <div className="flex flex-col items-center gap-4">
        <div className={`flex items-center justify-center size-16 rounded-full ${iconBgColor} ${iconTextColor}`}>
          {icon}
        </div>
        <div className="flex flex-col items-center gap-1">
          <h3 className="text-xl font-bold text-[#181411] dark:text-white">{title}</h3>
          {renderStatusBadge()}
        </div>
      </div>
      {renderActionButtons()}
    </motion.div>
  );
};

export default IntegrationCard;